import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface UIState {
  isLoading: boolean;
  error: string | null;
  notifications: Notification[];
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  rulesModalOpen: boolean;
  ageModalOpen: boolean;
  editStakeOpen: boolean;
}

const initialState: UIState = {
  isLoading: false,
  error: null,
  notifications: [],
  sidebarOpen: true,
  sidebarCollapsed: false,
  rulesModalOpen: false,
  ageModalOpen: false,
  editStakeOpen: false,
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotification: (state) => {
      state.notifications = [];
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setRulesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.rulesModalOpen = action.payload;
    },
    toggleRulesModalOpen: (state) => {
      state.rulesModalOpen = !state.rulesModalOpen;
    },
    setAgeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ageModalOpen = action.payload;
    },
    toggleAgeModalOpen: (state) => {
      state.ageModalOpen = !state.rulesModalOpen;
    },
    setEditStake: (state, action: PayloadAction<boolean>) => {
      state.editStakeOpen = action.payload;
    },
    toggleEditStake: (state) => {
      state.editStakeOpen = !state.editStakeOpen;
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  setNotification,
  removeNotification,
  clearNotification,
  setSidebarOpen,
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  setRulesModalOpen,
  toggleRulesModalOpen,
  setAgeModalOpen,
  toggleAgeModalOpen,
  setEditStake,
  toggleEditStake,
} = uiSlice.actions;
